import React from 'react'
import {FormattedMessage} from 'react-intl'
import {cx} from '@emotion/css'
import styled from '@emotion/styled'

import {Box} from '@vio/atlas/helpers/Box'
import {Image} from '@vio/atlas/Image'
import {truncateSingleLine} from '@vio/atlas/src/utils/truncate'
import {StarRating} from '@vio/atlas/StarRating'
import {Text} from '@vio/atlas/Text'
import {cssTheme} from '@vio/atlas/themes'
import {useBrandCapabilities} from '@vio/core/src/_web/brand/hooks/useBrandCapabilities'
import {hasAnchorPrice} from '@vio/core/src/offer/business/price'
import {SearchOffer} from '@vio/core/src/offer/types/SearchOffer'
import {ImageProvider} from '@vio/core/src/staticContent/types/ImageProvider'
import {getRoundingStrategy, RoundingStrategy} from '@vio/core/src/utils/number'

import {Cashback} from '../../cashback/components/Cashback'
import {HOME_PAGE_HOTEL_CARD_WIDTH} from '../constants'
import {PriceComposition} from '../PriceComposition'
import {ReviewRating} from '../ReviewRating'
import {PriceDetailsSkeleton} from './SuggestionHotelCardSkeleton'

const ClickArea = styled.div`
  background-color: ${cssTheme.colors.background.neutral.primary};
  border: 1px solid ${cssTheme.colors.border.neutral.secondary};
  display: flex;
  flex-direction: column;
  border-radius: ${cssTheme.layout.radius.lg};
  flex-grow: 1;
  cursor: pointer;
`

const TopSection = styled.section`
  display: flex;
  flex-grow: 1;
  flex-direction: column;
  border-radius: ${cssTheme.layout.radius.lg} ${cssTheme.layout.radius.lg} 0 0;
  overflow: hidden;
  z-index: 1;
`

const GalleryAndDetailsComponentWrapper = styled.div`
  display: flex;
  flex-direction: column;
  flex-grow: 1;
`

const BottomSection = styled.section`
  border-radius: ${cssTheme.layout.radius.lg};
  overflow: hidden;
  margin-top: ${cssTheme.layout.spacing.s400};
`

const GalleryComponentWrapper = styled.div`
  position: relative;
  height: 153px;
  width: 100%;
`

const PropertyDetailsComponentWrapper = styled.div`
  display: flex;
  flex-direction: column;
  flex-grow: 1;
  padding: 0 ${cssTheme.layout.spacing.s300} ${cssTheme.layout.spacing.s300};
`

const PropertyNameAndRating = styled.div`
  margin-bottom: ${cssTheme.layout.spacing.s200};
`

const NameAndRatingWrapper = styled.div`
  margin-bottom: ${cssTheme.layout.spacing.s200};
`

const HotelName = styled(Text)`
  ${truncateSingleLine()}
  width: 210px;
`

const Wrapper = styled.div`
  display: flex;
  width: ${HOME_PAGE_HOTEL_CARD_WIDTH};
  flex-shrink: 0;
  position: relative;
  &.--has-inside-margin {
    &:last-of-type {
      margin-right: ${cssTheme.layout.spacing.s500};
    }
    &:first-of-type {
      margin-left: ${cssTheme.layout.spacing.s500};
    }
  }
`

const PriceWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  margin-top: auto;
`

interface Props {
  hotelName: string
  starRating?: number
  reviewRating?: number
  reviewRatingCount?: number
  imageUrl?: string
  price: number
  anchorPrice?: number
  currency: string
  roundingStrategy?: RoundingStrategy
  imageProvider: ImageProvider
  onHotelClick?: (event: React.MouseEvent) => void
  offerDDActionName?: string
  isPrivateDeal?: boolean
  hasInsideMargin?: boolean
  cashback?: SearchOffer['cashback']
}

export const SuggestionHotelCard = ({
  hotelName,
  starRating,
  reviewRating,
  reviewRatingCount,
  imageUrl = '',
  currency,
  price,
  imageProvider,
  anchorPrice = 0,
  roundingStrategy = getRoundingStrategy(),
  onHotelClick,
  offerDDActionName,
  hasInsideMargin = false,
  cashback
}: Props) => {
  const {
    monetisation: {cashback: cashbackCapability}
  } = useBrandCapabilities()
  const isLongMessageLayout =
    cashbackCapability.enabled && cashbackCapability.config.isLongMessageLayout

  return (
    <Wrapper
      data-dd-action-name={offerDDActionName}
      className={cx(hasInsideMargin && '--has-inside-margin')}
    >
      <ClickArea role="button" tabIndex={0} onClick={onHotelClick}>
        <TopSection>
          <GalleryAndDetailsComponentWrapper>
            <GalleryComponentWrapper>
              <Image
                imageProvider={imageProvider}
                url={imageUrl}
                isResponsive
                size="large"
              />
            </GalleryComponentWrapper>
          </GalleryAndDetailsComponentWrapper>
        </TopSection>
        <BottomSection>
          <PropertyDetailsComponentWrapper>
            <PropertyNameAndRating>
              <NameAndRatingWrapper>
                {starRating && <StarRating rating={starRating} size="xs" />}
                <HotelName variant="labelM" dir="auto">
                  {hotelName}
                </HotelName>
              </NameAndRatingWrapper>
              <ReviewRating
                rating={reviewRating ?? 0}
                totalRatingCount={reviewRatingCount ?? 0}
              />
            </PropertyNameAndRating>

            <Box
              borderWidth="bw200"
              borderColor="content.neutral.secondary"
              display="flex"
              flexDirection="row"
              justifyContent={
                cashback && !isLongMessageLayout ? 'space-between' : 'flex-end'
              }
              alignItems="flex-end"
            >
              {cashback && !isLongMessageLayout && (
                <Cashback value={cashback.value} type={cashback.type} />
              )}

              <PriceWrapper>
                <Text variant="bodyXS" colorPath="content.neutral.secondary">
                  <FormattedMessage
                    id="suggestionHotelCard.priceLabel"
                    defaultMessage="Nightly from"
                  />
                </Text>
                {price ? (
                  <PriceComposition
                    size="xl"
                    showRowLayout
                    showAnchorPrice={hasAnchorPrice(price, anchorPrice)}
                    anchorPrice={anchorPrice}
                    currencyCode={currency}
                    price={price}
                    roundingStrategy={roundingStrategy}
                  />
                ) : (
                  <PriceDetailsSkeleton />
                )}
                {cashback && isLongMessageLayout && (
                  <Cashback value={cashback.value} type={cashback.type} />
                )}
              </PriceWrapper>
            </Box>
          </PropertyDetailsComponentWrapper>
        </BottomSection>
      </ClickArea>
    </Wrapper>
  )
}
